import { Controller } from "@hotwired/stimulus"

/**
 * Tabs Controller
 *
 * Provides simple tab switching functionality with URL hash persistence.
 * Tabs are shown/hidden based on data-tabs-target attributes.
 *
 * @example HTML structure
 *   <div data-controller="tabs" data-tabs-default-value="list">
 *     <!-- Tab buttons -->
 *     <button data-tabs-target="button" data-tab="list" data-action="tabs#switch">List</button>
 *     <button data-tabs-target="button" data-tab="map" data-action="tabs#switch">Map</button>
 *
 *     <!-- Tab panels -->
 *     <div data-tabs-target="panel" data-tab="list">List content</div>
 *     <div data-tabs-target="panel" data-tab="map">Map content</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["button", "panel"]
  static values = { default: String }

  connect() {
    // Check URL hash or use default
    const hash = window.location.hash.replace("#", "")
    const initialTab = hash || this.defaultValue || this.buttonTargets[0]?.dataset.tab
    
    if (initialTab) {
      this.showTab(initialTab)
    }
  }

  /**
   * Switches to the tab specified by the clicked button.
   *
   * @param {Event} event - The click event from the tab button
   */
  switch(event) {
    event.preventDefault()
    const tab = event.currentTarget.dataset.tab
    this.showTab(tab)
    
    // Update URL hash for bookmarking/sharing
    window.history.replaceState(null, null, `#${tab}`)
  }

  /**
   * Shows the specified tab and hides others.
   *
   * @param {string} tabName - The name of the tab to show
   */
  showTab(tabName) {
    // Update button states
    this.buttonTargets.forEach(button => {
      const isActive = button.dataset.tab === tabName
      button.classList.toggle("tabs-active", isActive)
      button.setAttribute("aria-selected", isActive)
    })

    // Update panel visibility
    this.panelTargets.forEach(panel => {
      const isActive = panel.dataset.tab === tabName
      panel.classList.toggle("hidden", !isActive)
      
      // Dispatch event when panel becomes visible (useful for map initialization)
      if (isActive) {
        panel.dispatchEvent(new CustomEvent("tab:shown", { bubbles: true }))
      }
    })
  }
}


